import React, { useEffect, useMemo, useState } from "react";
import { Grid, Card, CardContent, Typography, CircularProgress, Button, TextField, Dialog, DialogTitle, DialogContent, List, ListItem, ListItemText } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { keyframes } from "@mui/system";
import Navbar from "./Navbar";
import Logger from "./Logger";
import { getLoggerBaseURL } from "../utils/getLoggerBaseURL";

const bounceAnimation = keyframes`
  0% { transform: translateY(0); }
  50% { transform: translateY(4px); }
  100% { transform: translateY(0); }
`;

const PackerSummaries = () => {
    const [summaries, setSummaries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [visibleSummaries, setVisibleSummaries] = useState(6);
    const [summaryOrders, setSummaryOrders] = useState([]);
    const [selectedSummary, setSelectedSummary] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const adminEmail = sessionStorage.getItem("adminEmail");
    const logger = useMemo(() => new Logger(getLoggerBaseURL()), []);

    useEffect(() => {
        const fetchSummaries = async () => {
            try {
                const response = await fetch(`/api/packer/getSummary/${adminEmail}`);

                if (!response.ok) {
                    throw new Error("Failed to fetch summaries");
                }

                const data = await response.json();
                setSummaries(data.Summaries || []);
            } catch (error) {
                logger.log(error.message, "ERROR");
            } finally {
                setLoading(false);
            }
        };

        fetchSummaries();
    }, [adminEmail, logger]);

    const filteredSummaries = summaries.filter((summary) =>
        summary?.Order_No?.toLowerCase().includes(
            searchTerm.toLowerCase()
        )
    );

    const handleSummaryClick = async (summary) => {
        setSelectedSummary(summary);
        setOpenDialog(true);
        setLoadingDetails(true);

        try {
            const response = await fetch(`/api/packer/${adminEmail}/${summary.Order_No}`);

            if (!response.ok) {
                throw new Error("Failed to fetch summary details");
            }

            const data = await response.json();
            setSummaryOrders(data.orders || []);
        } catch (error) {
            logger.log(error.message, "ERROR");
        } finally {
            setLoadingDetails(false);
        }
    };

    if (loading) {
        return (
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                    flexDirection: "column",
                }}
            >
                <CircularProgress />
                <Typography sx={{ marginTop: 2 }}>
                    Fetching summaries...
                </Typography>
            </div>
        );
    }

    return (
        <div>
            <Navbar />
            <div style={{ background: "#f5f7fb", minHeight: "100vh", padding: "20px" }} >
                {/* Header */}
                <div style={{ marginBottom: "20px", marginTop: "50px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "15px" }} >
                    <Typography sx={{ fontWeight: "bold", fontSize: "25px", lineHeight: "40px", color: "#111827" }} >
                        Summaries
                    </Typography>

                    <TextField
                        size="small"
                        placeholder="Search summaries..."
                        value={searchTerm}
                        onChange={(e) =>
                            setSearchTerm(e.target.value)
                        }
                        sx={{
                            background: "white",
                            minWidth: "260px",
                            borderRadius: "10px",
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "10px",
                            },
                        }}
                    />
                </div>

                {/* Summaries Grid */}
                <Grid container spacing={3}>
                    {filteredSummaries
                        .slice(0, visibleSummaries)
                        .map((summary, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index} >
                                <Card
                                    sx={{
                                        borderRadius: "18px",
                                        border: "1px solid #e5e7eb",
                                        boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
                                        height: "100%",
                                        transition: "transform 0.2s ease, box-shadow 0.2s ease",
                                        "&:hover": {
                                            transform: "translateY(-3px)",
                                            boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                                        },
                                    }}
                                >
                                    <CardContent sx={{ padding: "22px", display: "flex", flexDirection: "column", justifyContent: "space-between", height: "100%" }} >
                                        <div>
                                            <Typography sx={{ fontSize: "13px", color: "#6b7280", marginBottom: "6px" }} >
                                                Summary Number
                                            </Typography>

                                            <Typography sx={{ fontSize: "18px", fontWeight: 700, color: "#111827" }} >
                                                {summary.Order_No}
                                            </Typography>

                                            <div style={{ marginTop: "18px" }} >
                                                <Typography sx={{ fontSize: "13px", color: "#6b7280" }} >
                                                    Company
                                                </Typography>

                                                <Typography sx={{ fontSize: "15px", fontWeight: 500, color: "#374151", marginTop: "4px" }} >
                                                    {summary.Comp_Name ||
                                                        "N/A"}
                                                </Typography>
                                            </div>
                                        </div>

                                        <Button
                                            onClick={() => handleSummaryClick(summary)}
                                            variant="contained"
                                            sx={{
                                                marginTop: "24px",
                                                backgroundColor: "#f59e0b",
                                                color: "white",
                                                borderRadius: "10px",
                                                textTransform: "none",
                                                height: "42px",
                                                fontWeight: 600,
                                                boxShadow: "none",
                                                "&:hover": {
                                                    backgroundColor: "#d97706",
                                                    boxShadow: "none",
                                                },
                                            }}
                                        >
                                            View Details
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                </Grid>

                {/* Load More */}
                {visibleSummaries <
                    filteredSummaries.length && (
                        <Button
                            onClick={() => setVisibleSummaries((prev) => prev + 6)}
                            sx={{
                                margin: "35px auto 10px auto",
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                background: "white",
                                border: "1px solid #d1d5db",
                                color: "#374151",
                                borderRadius: "12px",
                                textTransform: "none",
                                padding: "10px 18px",
                                fontWeight: 600,
                                "&:hover": {
                                    background: "#f9fafb",
                                    border: "1px solid #9ca3af",
                                },
                            }}
                        >
                            Load More Summaries

                            <KeyboardArrowDownIcon sx={{ animation: `${bounceAnimation} 1s infinite` }} />
                        </Button>
                    )}
            </div>

            {/* Details Dialog */}
            <Dialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: "18px",
                        padding: "4px",
                    },
                }}
            >
                <DialogTitle sx={{ fontWeight: 700, fontSize: "20px", color: "#111827", paddingBottom: "8px" }} >
                    Summary Details
                </DialogTitle>

                <DialogContent>
                    <Typography sx={{ fontSize: "14px", color: "#6b7280", marginBottom: "20px" }} >
                        Orders under summary:
                        <span style={{ fontWeight: 600, color: "#111827", marginLeft: "6px" }} >
                            {selectedSummary?.Order_No}
                        </span>
                    </Typography>

                    {loadingDetails ? (
                        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "40px 0" }}  >
                            <CircularProgress
                                size={28}
                            />
                        </div>
                    ) : summaryOrders.length > 0 ? (
                        <List sx={{ padding: 0 }} >
                            {summaryOrders.map(
                                (order, index) => (
                                    <ListItem
                                        key={order.Order_No}
                                        sx={{
                                            padding: "14px 0",
                                            borderBottom:
                                                index !==
                                                    summaryOrders.length -
                                                    1
                                                    ? "1px solid #f3f4f6"
                                                    : "none",
                                        }}
                                    >
                                        <ListItemText
                                            primary={
                                                <Typography sx={{ fontSize: "15px", fontWeight: 600, color: "#111827" }} >
                                                    {order.Order_No}
                                                </Typography>
                                            }
                                        />
                                    </ListItem>
                                )
                            )}
                        </List>
                    ) : (
                        <Typography sx={{ color: "#6b7280", textAlign: "center", padding: "20px 0" }} >
                            No orders found.
                        </Typography>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default PackerSummaries;